import { useCallback, useEffect } from "react";

import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";

import FloatingButtonWrapper from "@/ui/core/buttons/FloatingButtonWrapper";
import type { IconButtonProps } from "@/ui/core/buttons/IconButton";
import IconButton from "@/ui/core/buttons/IconButton";

interface FloatingActionButtonProps extends Pick<
  IconButtonProps,
  "accessibilityLabel" | "iconName" | "onPress" | "testID"
> {
  onAnimationComplete?: () => void;
  shouldAnimateBounce?: boolean;
}

const FloatingActionButton = ({
  accessibilityLabel,
  iconName,
  onAnimationComplete,
  onPress,
  shouldAnimateBounce = false,
  testID,
}: FloatingActionButtonProps) => {
  const translateY = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  const handlePress = useCallback(() => {
    cancelAnimation(translateY);
    translateY.value = 0;
    onAnimationComplete?.();
    onPress();
  }, [translateY, onPress, onAnimationComplete]);

  // unfortunately this can not be partially split out into its own function, due to the intricacies of reanimated/runWithJS/threading
  const triggerBounceAnimation = useCallback(() => {
    const easingConfig = Easing.out(Easing.ease);

    // Single bounce cycle
    const singleBounce = () => [
      // 1. First bounce - highest (slower ascent and descent)
      withTiming(-40, { duration: 350, easing: easingConfig }),
      withTiming(0, { duration: 300, easing: easingConfig }),

      // 2. Second bounce - medium height
      withTiming(-25, { duration: 250, easing: easingConfig }),
      withTiming(0, { duration: 200, easing: easingConfig }),

      // 3. Third bounce - lowest height
      withTiming(-12, { duration: 150, easing: easingConfig }),
      withTiming(0, { duration: 120, easing: easingConfig }),
    ];

    translateY.value = withSequence(
      ...singleBounce(),
      withTiming(0, { duration: 500 }),
      ...singleBounce(),
      withTiming(0, { duration: 0 }, (isFinished) => {
        if (isFinished && onAnimationComplete) {
          scheduleOnRN(onAnimationComplete);
        }
      }),
    );
  }, [translateY, onAnimationComplete]);

  // Trigger animation when shouldAnimateBounce becomes true
  useEffect(() => {
    if (shouldAnimateBounce) {
      triggerBounceAnimation();
    }
  }, [shouldAnimateBounce, triggerBounceAnimation]);

  return (
    <FloatingButtonWrapper alignSelf="flex-end" isViewportFixed right={20}>
      <Animated.View style={animatedStyle}>
        <IconButton
          accessibilityLabel={`${accessibilityLabel} Floating Action Button`}
          iconName={iconName}
          onPress={handlePress}
          testID={testID}
          variant="accent.blue"
        />
      </Animated.View>
    </FloatingButtonWrapper>
  );
};

export default FloatingActionButton;
